import React from "react";
import { FaChevronLeft } from "react-icons/fa";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";
import { accessibleShade } from "../accessibleColor";

const Header = styled.header<SpaceProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 24px;
  ${space};
`;

const Heading = styled.div`
  min-width: 0;
  flex: 1 1 320px;
`;

const Eyebrow = styled.p`
  margin: 0 0 4px;
  font-size: 13px;
  color: ${(p) => p.theme.colors.textMuted ?? p.theme.colors.grey1};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  line-height: 1.25;
  font-weight: 700;
  color: ${(p) => p.theme.colors.black};
  overflow-wrap: anywhere;

  @media (min-width: 640px) {
    font-size: 28px;
  }
`;

const Description = styled.div`
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: ${(p) => p.theme.colors.textMuted ?? p.theme.colors.grey1};
  max-width: 70ch;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => accessibleShade(p.theme.colors.primary)};

  & svg {
    font-size: 10px;
  }
`;

export type PageHeaderProps = {
  title: React.ReactNode;
  /** One line under the title. */
  description?: React.ReactNode;
  /** A small line above the title, e.g. today's date. */
  eyebrow?: React.ReactNode;
  /** Shown at the right of the title, e.g. a help button. */
  titleAdornment?: React.ReactNode;
  /** A "Back" link above the title. */
  back?: { to: string; label?: string };
  /** Buttons at the right. Keep to one primary action. */
  actions?: React.ReactNode;
} & SpaceProps;

/** The top of a page: title, one-line description and the page's actions. */
export function PageHeader({
  title,
  description,
  eyebrow,
  titleAdornment,
  back,
  actions,
  ...rest
}: PageHeaderProps) {
  return (
    <Header {...rest}>
      <Heading>
        {back && (
          <BackLink to={back.to}>
            <FaChevronLeft aria-hidden="true" />
            {back.label ?? "Back"}
          </BackLink>
        )}
        {eyebrow != null && <Eyebrow>{eyebrow}</Eyebrow>}
        <TitleRow>
          <Title>{title}</Title>
          {titleAdornment}
        </TitleRow>
        {description != null && <Description>{description}</Description>}
      </Heading>
      {actions != null && <Actions>{actions}</Actions>}
    </Header>
  );
}

PageHeader.defaultProps = { mb: 4 };
